import type {
  RecordingIntakeAgentMapping,
  RecordingIntakeAgentMappingResponse,
  RecordingS3IntakeUploadCredentials,
  RecordingS3IntakeUploadCredentialsResponse,
} from "@repo/app-types/entities/recordingIntake";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The customer-facing "Recording S3 intake" surface. The integration row
 * itself rides the boot payload (see `selectRecordingS3IntakeIntegration`);
 * these endpoints back the Settings tab's agent-email mapping and
 * uploader-credentials reveal. The Failed imports tab reads the generic
 * recording-import endpoints (`recordingImport.ts`).
 */

// The agent-email map is read-only and never invalidated.
const AGENT_MAP_TAG_ID = "recording-s3-intake-agent-map";

export const recordingS3IntakeIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // The S3 uploader credentials, decrypted on demand. Fetched lazily by the
    // "Reveal" control only, and never kept around: `keepUnusedDataFor: 0`
    // drops the secret from the store as soon as the component unmounts.
    recordingS3IntakeUploadCredentials: builder.query<
      RecordingS3IntakeUploadCredentials,
      void
    >({
      query: () => "/integrations/recording-s3-intake/upload-credentials",
      transformResponse: (
        response: RecordingS3IntakeUploadCredentialsResponse,
      ) => response.data,
      keepUnusedDataFor: 0,
    }),

    // The read-only RingCX agent username → teammate email map (RingCX customers
    // only). No live-refresh action, unlike the HQ tooling twin.
    recordingS3IntakeAgentEmailMap: builder.query<
      RecordingIntakeAgentMapping,
      void
    >({
      query: () => "/integrations/recording-s3-intake/agent-email-map",
      transformResponse: (response: RecordingIntakeAgentMappingResponse) =>
        response.data,
      providesTags: () => [
        buildEntityTag(TagType.RecordingS3IntakeIntegration, AGENT_MAP_TAG_ID),
      ],
    }),
  }),
});

export const {
  useRecordingS3IntakeAgentEmailMapQuery,
  useLazyRecordingS3IntakeUploadCredentialsQuery,
} = recordingS3IntakeIntegrationApi;
